/**
 * The registry the BUILDER renders from: every section's schema, presets and meta up front, and its render
 * code only when a page actually asks for it.
 *
 * ── Why a second registry exists ───────────────────────────────────────────────────────────────────────
 *
 * {@link ./registry.gen} is right for the storefront. Astro renders a page in one synchronous pass on the
 * server, where holding all sixty-two components costs nothing. The builder is the opposite case: it runs in
 * the seller's browser, over the seller's line, and it opens ONE page — five to ten sections. It was paying
 * for sixty-two. Measured by sourcemap attribution on the preprod entry chunk (2026-09-16),
 * `sections/*` + `blocks/*` render code was 2.1 MB of the 5.5 MB that chunk is built from, and the editor
 * spent twenty-five seconds on a spinner before React mounted.
 *
 * Schemas, presets and meta stay EAGER on purpose. The inspector, the section rail, `hydrateDocument` and
 * "Add section" all read them synchronously, for sections that are not on the page and may never be. They
 * are data, they are what `schema.ts` holds, and splitting them would buy less and cost every call site an
 * await. Only `Component` — the JSX, the hooks, the engines — is deferred.
 *
 * ── How a component arrives ────────────────────────────────────────────────────────────────────────────
 *
 * Each entry's `Component` is a STABLE dispatcher created once at module init. It looks its real component
 * up in {@link loadedComponents} at render time and, until it is there, paints nothing rather than throwing.
 * Stable identity is what keeps `MemoSection`'s memoization honest — a registry that swapped component
 * identities on load would re-render every section on the page (see `granularRender.test.tsx`).
 *
 * Nothing is expected to render against that empty state: the host AWAITS {@link loadSectionComponents} for
 * the types in a document before it puts that document on screen, so the first paint is already complete.
 * The blank is the floor under a chunk that failed to arrive, not a loading state.
 *
 * ── GENERATED-SHAPED, hand-maintained ──────────────────────────────────────────────────────────────────
 *
 * Like `registry.gen.ts`, and in the SAME order, so the two read side by side. Adding a section means a line
 * in both — and `registry.parity.test.ts` fails the build if you add it to only one, or if the two disagree
 * about a type, schema, presets or meta.
 */
import { createElement } from 'react';
// Re-exported so a host wires the lazy registry in ONE import: take the registry and the registrar from the
// same module it is choosing. The eager side has its own module for this (`eager-registry.ts`).
export { registerDefaultSectionRegistry } from './default-section-registry.js';
import { bundleDealsMeta, bundleDealsPresets, bundleDealsSchema, } from '../sections/bundle-deals/schema.js';
import { cartFooterMeta, cartFooterPresets, cartFooterSchema, } from '../sections/cart-footer/schema.js';
import { cartItemsMeta, cartItemsPresets, cartItemsSchema } from '../sections/cart-items/schema.js';
import { buyBoxMeta, buyBoxPresets, buyBoxSchema } from '../sections/buy-box/schema.js';
import { codBuyboxMeta, codBuyboxPresets, codBuyboxSchema } from '../sections/cod-buybox/schema.js';
import { collageMeta, collagePresets, collageSchema } from '../sections/collage/schema.js';
import { collectionBannerMeta, collectionBannerPresets, collectionBannerSchema, } from '../sections/collection-banner/schema.js';
import { collectionGridMeta, collectionGridPresets, collectionGridSchema, } from '../sections/collection-grid/schema.js';
import { collapsibleContentMeta, collapsibleContentPresets, collapsibleContentSchema, } from '../sections/collapsible-content/schema.js';
import { collectionListMeta, collectionListPresets, collectionListSchema, } from '../sections/collection-list/schema.js';
import { orderConfirmationMeta, orderConfirmationPresets, orderConfirmationSchema, } from '../sections/order-confirmation/schema.js';
import { checkoutMeta, checkoutPresets, checkoutSchema } from '../sections/checkout/schema.js';
import { welcomePopupMeta, welcomePopupPresets, welcomePopupSchema, } from '../sections/welcome-popup/schema.js';
import { comparisonSliderMeta, comparisonSliderPresets, comparisonSliderSchema, } from '../sections/comparison-slider/schema.js';
import { comparisonTableMeta, comparisonTablePresets, comparisonTableSchema, } from '../sections/comparison-table/schema.js';
import { specComparisonMeta, specComparisonPresets, specComparisonSchema, } from '../sections/spec-comparison/schema.js';
import { anchorNavMeta, anchorNavPresets, anchorNavSchema } from '../sections/anchor-nav/schema.js';
import { contactFormMeta, contactFormPresets, contactFormSchema, } from '../sections/contact-form/schema.js';
import { customColumnsMeta, customColumnsPresets, customColumnsSchema, } from '../sections/custom-columns/schema.js';
import { customColumnsNewMeta, customColumnsNewPresets, customColumnsNewSchema, } from '../sections/custom-columns-new/schema.js';
import { customHtmlMeta, customHtmlPresets, customHtmlSchema, } from '../sections/custom-html/schema.js';
import { facebookTestimonialsMeta, facebookTestimonialsPresets, facebookTestimonialsSchema, } from '../sections/facebook-testimonials/schema.js';
import { faqMeta, faqPresets, faqSchema } from '../sections/faq/schema.js';
import { iconBarMeta, iconBarPresets, iconBarSchema } from '../sections/icon-bar/schema.js';
import { iconsWithContentMeta, iconsWithContentPresets, iconsWithContentSchema, } from '../sections/icons-with-content/schema.js';
import { imageMeta, imagePresets, imageSchema } from '../sections/image/schema.js';
import { imageSliderMeta, imageSliderPresets, imageSliderSchema, } from '../sections/image-slider/schema.js';
import { imageRailMeta, imageRailPresets, imageRailSchema } from '../sections/image-rail/schema.js';
import { featureGalleryMeta, featureGalleryPresets, featureGallerySchema, } from '../sections/feature-gallery/schema.js';
import { flipCardsMeta, flipCardsPresets, flipCardsSchema } from '../sections/flip-cards/schema.js';
import { colorwaysMeta, colorwaysPresets, colorwaysSchema } from '../sections/colorways/schema.js';
import { logoListMeta, logoListPresets, logoListSchema } from '../sections/logo-list/schema.js';
import { resultsMeta, resultsPresets, resultsSchema } from '../sections/results/schema.js';
import { slideshowMeta, slideshowPresets, slideshowSchema } from '../sections/slideshow/schema.js';
import { featuredBlogMeta, featuredBlogPresets, featuredBlogSchema, } from '../sections/featured-blog/schema.js';
import { featuredCollectionMeta, featuredCollectionPresets, featuredCollectionSchema, } from '../sections/featured-collection/schema.js';
import { featuredProductMeta, featuredProductPresets, featuredProductSchema, } from '../sections/featured-product/schema.js';
import { footerMeta, footerPresets, footerSchema } from '../sections/footer/schema.js';
import { headerMeta, headerPresets, headerSchema } from '../sections/header/schema.js';
import { announcementBarMeta, announcementBarPresets, announcementBarSchema, } from '../sections/announcement-bar/schema.js';
import { countdownMeta, countdownPresets, countdownSchema } from '../sections/countdown/schema.js';
import { horizontalTickerMeta, horizontalTickerPresets, horizontalTickerSchema, } from '../sections/horizontal-ticker/schema.js';
import { heroMeta, heroPresets, heroSchema } from '../sections/hero/schema.js';
import { imageWithTextMeta, imageWithTextPresets, imageWithTextSchema, } from '../sections/image-with-text/schema.js';
import { instaStoriesMeta, instaStoriesPresets, instaStoriesSchema, } from '../sections/insta-stories/schema.js';
import { mainProductMeta, mainProductPresets, mainProductSchema, } from '../sections/main-product/schema.js';
import { multirowMeta, multirowPresets, multirowSchema } from '../sections/multirow/schema.js';
import { newsletterMeta, newsletterPresets, newsletterSchema, } from '../sections/newsletter/schema.js';
import { parallaxHeroMeta, parallaxHeroPresets, parallaxHeroSchema, } from '../sections/parallax-hero/schema.js';
import { productGalleryMeta, productGalleryPresets, productGallerySchema, } from '../sections/product-gallery/schema.js';
import { relatedProductsMeta, relatedProductsPresets, relatedProductsSchema, } from '../sections/related-products/schema.js';
import { richTextMeta, richTextPresets, richTextSchema } from '../sections/rich-text/schema.js';
import { sectionDividerMeta, sectionDividerPresets, sectionDividerSchema, } from '../sections/section-divider/schema.js';
import { shoppableImageMeta, shoppableImagePresets, shoppableImageSchema, } from '../sections/shoppable-image/schema.js';
import { stickyCtaMeta, stickyCtaPresets, stickyCtaSchema } from '../sections/sticky-cta/schema.js';
import { slideshowHeroMeta, slideshowHeroPresets, slideshowHeroSchema, } from '../sections/slideshow-hero/schema.js';
import { productReviewsMeta, productReviewsPresets, productReviewsSchema, } from '../sections/product-reviews/schema.js';
import { testimonialsMeta, testimonialsPresets, testimonialsSchema, } from '../sections/testimonials/schema.js';
import { timelineMeta, timelinePresets, timelineSchema } from '../sections/timeline/schema.js';
import { tiktokVideosMeta, tiktokVideosPresets, tiktokVideosSchema, } from '../sections/tiktok-videos/schema.js';
import { trackOrderMeta, trackOrderPresets, trackOrderSchema, } from '../sections/track-order/schema.js';
import { trustpilotReviewsMeta, trustpilotReviewsPresets, trustpilotReviewsSchema, } from '../sections/trustpilot-reviews/schema.js';
import { tabbedContentMeta, tabbedContentPresets, tabbedContentSchema, } from '../sections/tabbed-content/schema.js';
import { trustRowMeta, trustRowPresets, trustRowSchema } from '../sections/trust-row/schema.js';
import { verticalTickerMeta, verticalTickerPresets, verticalTickerSchema, } from '../sections/vertical-ticker/schema.js';
import { videoMeta, videoPresets, videoSchema } from '../sections/video/schema.js';
import { videoWithTextMeta, videoWithTextPresets, videoWithTextSchema, } from '../sections/video-with-text/schema.js';
import { contentTabsMeta, contentTabsPresets, contentTabsSchema, } from '../sections/content-tabs/schema.js';
import { tabbedColumnsMeta, tabbedColumnsPresets, tabbedColumnsSchema, } from '../sections/tabbed-columns/schema.js';
/** Real components, keyed by section type, filled by {@link loadSectionComponents}. */
const loadedComponents = new Map();
/**
 * type → the dynamic import that yields its component. Literal specifiers, one per line: a bundler only
 * splits what it can see statically, and `import('../sections/' + folder + '/Component.js')` would emit a
 * glob that pulls all sixty-two back into the entry — the exact bill this module exists to stop paying.
 */
const LOADERS = {
    'bundle-deals': () => import('../sections/bundle-deals/Component.js').then((m) => m.BundleDeals),
    'cart-footer': () => import('../sections/cart-footer/Component.js').then((m) => m.CartFooter),
    'cart-items': () => import('../sections/cart-items/Component.js').then((m) => m.CartItems),
    'buy-box': () => import('../sections/buy-box/Component.js').then((m) => m.BuyBox),
    'cod-buybox': () => import('../sections/cod-buybox/Component.js').then((m) => m.CodBuybox),
    collage: () => import('../sections/collage/Component.js').then((m) => m.Collage),
    'collection-banner': () => import('../sections/collection-banner/Component.js').then((m) => m.CollectionBanner),
    'collection-grid': () => import('../sections/collection-grid/Component.js').then((m) => m.CollectionGrid),
    'collapsible-content': () => import('../sections/collapsible-content/Component.js').then((m) => m.CollapsibleContent),
    'collection-list': () => import('../sections/collection-list/Component.js').then((m) => m.CollectionList),
    'order-confirmation': () => import('../sections/order-confirmation/Component.js').then((m) => m.OrderConfirmation),
    checkout: () => import('../sections/checkout/Component.js').then((m) => m.Checkout),
    'welcome-popup': () => import('../sections/welcome-popup/Component.js').then((m) => m.WelcomePopup),
    'comparison-slider': () => import('../sections/comparison-slider/Component.js').then((m) => m.ComparisonSlider),
    'comparison-table': () => import('../sections/comparison-table/Component.js').then((m) => m.ComparisonTable),
    'spec-comparison': () => import('../sections/spec-comparison/Component.js').then((m) => m.SpecComparison),
    'anchor-nav': () => import('../sections/anchor-nav/Component.js').then((m) => m.AnchorNav),
    'contact-form': () => import('../sections/contact-form/Component.js').then((m) => m.ContactForm),
    'custom-columns': () => import('../sections/custom-columns/Component.js').then((m) => m.CustomColumns),
    'custom-columns-new': () => import('../sections/custom-columns-new/Component.js').then((m) => m.CustomColumnsNew),
    'custom-html': () => import('../sections/custom-html/Component.js').then((m) => m.CustomHtml),
    'facebook-testimonials': () => import('../sections/facebook-testimonials/Component.js').then((m) => m.FacebookTestimonials),
    faq: () => import('../sections/faq/Component.js').then((m) => m.Faq),
    'icon-bar': () => import('../sections/icon-bar/Component.js').then((m) => m.IconBar),
    'icons-with-content': () => import('../sections/icons-with-content/Component.js').then((m) => m.IconsWithContent),
    image: () => import('../sections/image/Component.js').then((m) => m.Image),
    'image-slider': () => import('../sections/image-slider/Component.js').then((m) => m.ImageSlider),
    'image-rail': () => import('../sections/image-rail/Component.js').then((m) => m.ImageRail),
    'flip-cards': () => import('../sections/flip-cards/Component.js').then((m) => m.FlipCards),
    'feature-gallery': () => import('../sections/feature-gallery/Component.js').then((m) => m.FeatureGallery),
    colorways: () => import('../sections/colorways/Component.js').then((m) => m.Colorways),
    'logo-list': () => import('../sections/logo-list/Component.js').then((m) => m.LogoList),
    results: () => import('../sections/results/Component.js').then((m) => m.Results),
    slideshow: () => import('../sections/slideshow/Component.js').then((m) => m.Slideshow),
    'featured-blog': () => import('../sections/featured-blog/Component.js').then((m) => m.FeaturedBlog),
    'featured-collection': () => import('../sections/featured-collection/Component.js').then((m) => m.FeaturedCollection),
    'featured-product': () => import('../sections/featured-product/Component.js').then((m) => m.FeaturedProduct),
    footer: () => import('../sections/footer/Component.js').then((m) => m.Footer),
    header: () => import('../sections/header/Component.js').then((m) => m.Header),
    'announcement-bar': () => import('../sections/announcement-bar/Component.js').then((m) => m.AnnouncementBar),
    countdown: () => import('../sections/countdown/Component.js').then((m) => m.Countdown),
    'horizontal-ticker': () => import('../sections/horizontal-ticker/Component.js').then((m) => m.HorizontalTicker),
    hero: () => import('../sections/hero/Component.js').then((m) => m.Hero),
    'image-with-text': () => import('../sections/image-with-text/Component.js').then((m) => m.ImageWithText),
    'insta-stories': () => import('../sections/insta-stories/Component.js').then((m) => m.InstaStories),
    'main-product': () => import('../sections/main-product/Component.js').then((m) => m.MainProduct),
    multirow: () => import('../sections/multirow/Component.js').then((m) => m.Multirow),
    newsletter: () => import('../sections/newsletter/Component.js').then((m) => m.Newsletter),
    'parallax-hero': () => import('../sections/parallax-hero/Component.js').then((m) => m.ParallaxHero),
    'product-gallery': () => import('../sections/product-gallery/Component.js').then((m) => m.ProductGallery),
    'related-products': () => import('../sections/related-products/Component.js').then((m) => m.RelatedProducts),
    'rich-text': () => import('../sections/rich-text/Component.js').then((m) => m.RichText),
    'section-divider': () => import('../sections/section-divider/Component.js').then((m) => m.SectionDivider),
    'shoppable-image': () => import('../sections/shoppable-image/Component.js').then((m) => m.ShoppableImage),
    'sticky-cta': () => import('../sections/sticky-cta/Component.js').then((m) => m.StickyCta),
    'slideshow-hero': () => import('../sections/slideshow-hero/Component.js').then((m) => m.SlideshowHero),
    'product-reviews': () => import('../sections/product-reviews/Component.js').then((m) => m.ProductReviewsSection),
    testimonials: () => import('../sections/testimonials/Component.js').then((m) => m.Testimonials),
    timeline: () => import('../sections/timeline/Component.js').then((m) => m.Timeline),
    'tiktok-videos': () => import('../sections/tiktok-videos/Component.js').then((m) => m.TiktokVideos),
    'track-order': () => import('../sections/track-order/Component.js').then((m) => m.TrackOrder),
    'trustpilot-reviews': () => import('../sections/trustpilot-reviews/Component.js').then((m) => m.TrustpilotReviews),
    'tabbed-content': () => import('../sections/tabbed-content/Component.js').then((m) => m.TabbedContent),
    'trust-row': () => import('../sections/trust-row/Component.js').then((m) => m.TrustRow),
    'vertical-ticker': () => import('../sections/vertical-ticker/Component.js').then((m) => m.VerticalTicker),
    video: () => import('../sections/video/Component.js').then((m) => m.Video),
    'video-with-text': () => import('../sections/image-with-text/Component.js').then((m) => m.ImageWithText),
    'content-tabs': () => import('../sections/content-tabs/Component.js').then((m) => m.ContentTabs),
    'tabbed-columns': () => import('../sections/tabbed-columns/Component.js').then((m) => m.TabbedColumns),
};
/**
 * A stable stand-in for one section type. Created ONCE per type at module init, so the registry's component
 * identities never change and memoized sections are not re-rendered by a load.
 */
function dispatcher(type) {
    const Dispatch = (props) => {
        const Real = loadedComponents.get(type);
        return Real ? createElement(Real, props) : null;
    };
    Dispatch.displayName = `LazySection(${type})`;
    return Dispatch;
}
/**
 * Fetch the render code for [types] and make it renderable, then resolve.
 *
 * Await it BEFORE putting a document on screen. Every type is fetched in parallel, already-loaded and
 * in-flight types cost nothing, and a type with no loader (a document authored against a newer registry)
 * is skipped — `MemoSection` already labels an unknown section rather than failing.
 *
 * A chunk that fails to arrive resolves quietly and leaves that one section blank. The alternative is an
 * editor that refuses to open because one section of ten lost a request.
 */
export async function loadSectionComponents(types) {
    const wanted = [...new Set(types)]
        .filter((t) => !loadedComponents.has(t))
        .map((type) => [type, LOADERS[type]])
        .filter((pair) => pair[1] != null);
    await Promise.all(wanted.map(async ([type, load]) => {
        try {
            loadedComponents.set(type, await load());
        }
        catch {
            // Left unloaded on purpose: the next call retries rather than caching the failure.
        }
    }));
}
/**
 * The real component for [type], or undefined while its chunk is still out.
 *
 * For the parity test, which proves the deferred component is the SAME one the storefront renders. Render
 * paths go through the registry's dispatcher instead — it survives a type that never loaded.
 */
export function loadedSectionComponent(type) {
    return loadedComponents.get(type);
}
/**
 * Fetch the render code for EVERY section, in the background, once.
 *
 * The split above is about the FIRST paint: an editor that opens one page should not download sixty-one
 * sections to show ten. It is not about the next five minutes. Once the page is on screen the seller will
 * open "Add section", and a picker whose preview waits a round-trip per hover is a worse editor than the one
 * we started with — the pane sits on the section's glyph while the chunk travels, which on a thin line is
 * exactly the wait we just removed from the boot, moved somewhere else.
 *
 * So the rest streams in while the seller looks at their page. It resolves already-loaded types for free, and
 * a host should call it AFTER its first document is painted, from an idle callback — never on the boot path,
 * which would put all sixty-one back in front of the first paint by another road.
 */
export function warmAllSectionComponents() {
    return loadSectionComponents(Object.keys(LOADERS));
}
/** Whether [type]'s render code is already here — lets a caller skip an await it does not need. */
export function isSectionComponentLoaded(type) {
    return loadedComponents.has(type);
}
/** Every type this registry can render. Same set as `defaultRegistry`'s keys; the parity test proves it. */
export function lazySectionTypes() {
    return Object.keys(LOADERS);
}
/** The builder's registry. Same shape and order as `defaultRegistry`; only `Component` is deferred. */
export const lazyRegistry = {
    'bundle-deals': {
        type: 'bundle-deals',
        schema: bundleDealsSchema,
        Component: dispatcher('bundle-deals'),
        presets: bundleDealsPresets,
        meta: bundleDealsMeta,
    },
    'cart-footer': {
        type: 'cart-footer',
        schema: cartFooterSchema,
        Component: dispatcher('cart-footer'),
        presets: cartFooterPresets,
        meta: cartFooterMeta,
    },
    'cart-items': {
        type: 'cart-items',
        schema: cartItemsSchema,
        Component: dispatcher('cart-items'),
        presets: cartItemsPresets,
        meta: cartItemsMeta,
    },
    'buy-box': {
        type: 'buy-box',
        schema: buyBoxSchema,
        Component: dispatcher('buy-box'),
        presets: buyBoxPresets,
        meta: buyBoxMeta,
    },
    'cod-buybox': {
        type: 'cod-buybox',
        schema: codBuyboxSchema,
        Component: dispatcher('cod-buybox'),
        presets: codBuyboxPresets,
        meta: codBuyboxMeta,
    },
    collage: {
        type: 'collage',
        schema: collageSchema,
        Component: dispatcher('collage'),
        presets: collagePresets,
        meta: collageMeta,
    },
    'collection-banner': {
        type: 'collection-banner',
        schema: collectionBannerSchema,
        Component: dispatcher('collection-banner'),
        presets: collectionBannerPresets,
        meta: collectionBannerMeta,
    },
    'collection-grid': {
        type: 'collection-grid',
        schema: collectionGridSchema,
        Component: dispatcher('collection-grid'),
        presets: collectionGridPresets,
        meta: collectionGridMeta,
    },
    'collapsible-content': {
        type: 'collapsible-content',
        schema: collapsibleContentSchema,
        Component: dispatcher('collapsible-content'),
        presets: collapsibleContentPresets,
        meta: collapsibleContentMeta,
    },
    'collection-list': {
        type: 'collection-list',
        schema: collectionListSchema,
        Component: dispatcher('collection-list'),
        presets: collectionListPresets,
        meta: collectionListMeta,
    },
    'order-confirmation': {
        type: 'order-confirmation',
        schema: orderConfirmationSchema,
        Component: dispatcher('order-confirmation'),
        presets: orderConfirmationPresets,
        meta: orderConfirmationMeta,
    },
    checkout: {
        type: 'checkout',
        schema: checkoutSchema,
        Component: dispatcher('checkout'),
        presets: checkoutPresets,
        meta: checkoutMeta,
    },
    'welcome-popup': {
        type: 'welcome-popup',
        schema: welcomePopupSchema,
        Component: dispatcher('welcome-popup'),
        presets: welcomePopupPresets,
        meta: welcomePopupMeta,
    },
    'comparison-slider': {
        type: 'comparison-slider',
        schema: comparisonSliderSchema,
        Component: dispatcher('comparison-slider'),
        presets: comparisonSliderPresets,
        meta: comparisonSliderMeta,
    },
    'comparison-table': {
        type: 'comparison-table',
        schema: comparisonTableSchema,
        Component: dispatcher('comparison-table'),
        presets: comparisonTablePresets,
        meta: comparisonTableMeta,
    },
    'spec-comparison': {
        type: 'spec-comparison',
        schema: specComparisonSchema,
        Component: dispatcher('spec-comparison'),
        presets: specComparisonPresets,
        meta: specComparisonMeta,
    },
    'anchor-nav': {
        type: 'anchor-nav',
        schema: anchorNavSchema,
        Component: dispatcher('anchor-nav'),
        presets: anchorNavPresets,
        meta: anchorNavMeta,
    },
    'contact-form': {
        type: 'contact-form',
        schema: contactFormSchema,
        Component: dispatcher('contact-form'),
        presets: contactFormPresets,
        meta: contactFormMeta,
    },
    'custom-columns': {
        type: 'custom-columns',
        schema: customColumnsSchema,
        Component: dispatcher('custom-columns'),
        presets: customColumnsPresets,
        meta: customColumnsMeta,
    },
    'custom-columns-new': {
        type: 'custom-columns-new',
        schema: customColumnsNewSchema,
        Component: dispatcher('custom-columns-new'),
        presets: customColumnsNewPresets,
        meta: customColumnsNewMeta,
    },
    'custom-html': {
        type: 'custom-html',
        schema: customHtmlSchema,
        Component: dispatcher('custom-html'),
        presets: customHtmlPresets,
        meta: customHtmlMeta,
    },
    'facebook-testimonials': {
        type: 'facebook-testimonials',
        schema: facebookTestimonialsSchema,
        Component: dispatcher('facebook-testimonials'),
        presets: facebookTestimonialsPresets,
        meta: facebookTestimonialsMeta,
    },
    faq: {
        type: 'faq',
        schema: faqSchema,
        Component: dispatcher('faq'),
        presets: faqPresets,
        meta: faqMeta,
    },
    'icon-bar': {
        type: 'icon-bar',
        schema: iconBarSchema,
        Component: dispatcher('icon-bar'),
        presets: iconBarPresets,
        meta: iconBarMeta,
    },
    'icons-with-content': {
        type: 'icons-with-content',
        schema: iconsWithContentSchema,
        Component: dispatcher('icons-with-content'),
        presets: iconsWithContentPresets,
        meta: iconsWithContentMeta,
    },
    image: {
        type: 'image',
        schema: imageSchema,
        Component: dispatcher('image'),
        presets: imagePresets,
        meta: imageMeta,
    },
    'image-slider': {
        type: 'image-slider',
        schema: imageSliderSchema,
        Component: dispatcher('image-slider'),
        presets: imageSliderPresets,
        meta: imageSliderMeta,
    },
    'image-rail': {
        type: 'image-rail',
        schema: imageRailSchema,
        Component: dispatcher('image-rail'),
        presets: imageRailPresets,
        meta: imageRailMeta,
    },
    'feature-gallery': {
        type: 'feature-gallery',
        schema: featureGallerySchema,
        Component: dispatcher('feature-gallery'),
        presets: featureGalleryPresets,
        meta: featureGalleryMeta,
    },
    'flip-cards': {
        type: 'flip-cards',
        schema: flipCardsSchema,
        Component: dispatcher('flip-cards'),
        presets: flipCardsPresets,
        meta: flipCardsMeta,
    },
    colorways: {
        type: 'colorways',
        schema: colorwaysSchema,
        Component: dispatcher('colorways'),
        presets: colorwaysPresets,
        meta: colorwaysMeta,
    },
    'logo-list': {
        type: 'logo-list',
        schema: logoListSchema,
        Component: dispatcher('logo-list'),
        presets: logoListPresets,
        meta: logoListMeta,
    },
    results: {
        type: 'results',
        schema: resultsSchema,
        Component: dispatcher('results'),
        presets: resultsPresets,
        meta: resultsMeta,
    },
    slideshow: {
        type: 'slideshow',
        schema: slideshowSchema,
        Component: dispatcher('slideshow'),
        presets: slideshowPresets,
        meta: slideshowMeta,
    },
    'featured-blog': {
        type: 'featured-blog',
        schema: featuredBlogSchema,
        Component: dispatcher('featured-blog'),
        presets: featuredBlogPresets,
        meta: featuredBlogMeta,
    },
    'featured-collection': {
        type: 'featured-collection',
        schema: featuredCollectionSchema,
        Component: dispatcher('featured-collection'),
        presets: featuredCollectionPresets,
        meta: featuredCollectionMeta,
    },
    'featured-product': {
        type: 'featured-product',
        schema: featuredProductSchema,
        Component: dispatcher('featured-product'),
        presets: featuredProductPresets,
        meta: featuredProductMeta,
    },
    footer: {
        type: 'footer',
        schema: footerSchema,
        Component: dispatcher('footer'),
        presets: footerPresets,
        meta: footerMeta,
    },
    header: {
        type: 'header',
        schema: headerSchema,
        Component: dispatcher('header'),
        presets: headerPresets,
        meta: headerMeta,
    },
    'announcement-bar': {
        type: 'announcement-bar',
        schema: announcementBarSchema,
        Component: dispatcher('announcement-bar'),
        presets: announcementBarPresets,
        meta: announcementBarMeta,
    },
    countdown: {
        type: 'countdown',
        schema: countdownSchema,
        Component: dispatcher('countdown'),
        presets: countdownPresets,
        meta: countdownMeta,
    },
    'horizontal-ticker': {
        type: 'horizontal-ticker',
        schema: horizontalTickerSchema,
        Component: dispatcher('horizontal-ticker'),
        presets: horizontalTickerPresets,
        meta: horizontalTickerMeta,
    },
    hero: {
        type: 'hero',
        schema: heroSchema,
        Component: dispatcher('hero'),
        presets: heroPresets,
        meta: heroMeta,
    },
    'image-with-text': {
        type: 'image-with-text',
        schema: imageWithTextSchema,
        Component: dispatcher('image-with-text'),
        presets: imageWithTextPresets,
        meta: imageWithTextMeta,
    },
    'insta-stories': {
        type: 'insta-stories',
        schema: instaStoriesSchema,
        Component: dispatcher('insta-stories'),
        presets: instaStoriesPresets,
        meta: instaStoriesMeta,
    },
    'main-product': {
        type: 'main-product',
        schema: mainProductSchema,
        Component: dispatcher('main-product'),
        presets: mainProductPresets,
        meta: mainProductMeta,
    },
    multirow: {
        type: 'multirow',
        schema: multirowSchema,
        Component: dispatcher('multirow'),
        presets: multirowPresets,
        meta: multirowMeta,
    },
    newsletter: {
        type: 'newsletter',
        schema: newsletterSchema,
        Component: dispatcher('newsletter'),
        presets: newsletterPresets,
        meta: newsletterMeta,
    },
    'parallax-hero': {
        type: 'parallax-hero',
        schema: parallaxHeroSchema,
        Component: dispatcher('parallax-hero'),
        presets: parallaxHeroPresets,
        meta: parallaxHeroMeta,
    },
    'product-gallery': {
        type: 'product-gallery',
        schema: productGallerySchema,
        Component: dispatcher('product-gallery'),
        presets: productGalleryPresets,
        meta: productGalleryMeta,
    },
    'related-products': {
        type: 'related-products',
        schema: relatedProductsSchema,
        Component: dispatcher('related-products'),
        presets: relatedProductsPresets,
        meta: relatedProductsMeta,
    },
    'rich-text': {
        type: 'rich-text',
        schema: richTextSchema,
        Component: dispatcher('rich-text'),
        presets: richTextPresets,
        meta: richTextMeta,
    },
    'section-divider': {
        type: 'section-divider',
        schema: sectionDividerSchema,
        Component: dispatcher('section-divider'),
        presets: sectionDividerPresets,
        meta: sectionDividerMeta,
    },
    'shoppable-image': {
        type: 'shoppable-image',
        schema: shoppableImageSchema,
        Component: dispatcher('shoppable-image'),
        presets: shoppableImagePresets,
        meta: shoppableImageMeta,
    },
    'sticky-cta': {
        type: 'sticky-cta',
        schema: stickyCtaSchema,
        Component: dispatcher('sticky-cta'),
        presets: stickyCtaPresets,
        meta: stickyCtaMeta,
    },
    'slideshow-hero': {
        type: 'slideshow-hero',
        schema: slideshowHeroSchema,
        Component: dispatcher('slideshow-hero'),
        presets: slideshowHeroPresets,
        meta: slideshowHeroMeta,
    },
    'product-reviews': {
        type: 'product-reviews',
        schema: productReviewsSchema,
        Component: dispatcher('product-reviews'),
        presets: productReviewsPresets,
        meta: productReviewsMeta,
    },
    testimonials: {
        type: 'testimonials',
        schema: testimonialsSchema,
        Component: dispatcher('testimonials'),
        presets: testimonialsPresets,
        meta: testimonialsMeta,
    },
    timeline: {
        type: 'timeline',
        schema: timelineSchema,
        Component: dispatcher('timeline'),
        presets: timelinePresets,
        meta: timelineMeta,
    },
    'tiktok-videos': {
        type: 'tiktok-videos',
        schema: tiktokVideosSchema,
        Component: dispatcher('tiktok-videos'),
        presets: tiktokVideosPresets,
        meta: tiktokVideosMeta,
    },
    'track-order': {
        type: 'track-order',
        schema: trackOrderSchema,
        Component: dispatcher('track-order'),
        presets: trackOrderPresets,
        meta: trackOrderMeta,
    },
    'trustpilot-reviews': {
        type: 'trustpilot-reviews',
        schema: trustpilotReviewsSchema,
        Component: dispatcher('trustpilot-reviews'),
        presets: trustpilotReviewsPresets,
        meta: trustpilotReviewsMeta,
    },
    'tabbed-content': {
        type: 'tabbed-content',
        schema: tabbedContentSchema,
        Component: dispatcher('tabbed-content'),
        presets: tabbedContentPresets,
        meta: tabbedContentMeta,
    },
    'trust-row': {
        type: 'trust-row',
        schema: trustRowSchema,
        Component: dispatcher('trust-row'),
        presets: trustRowPresets,
        meta: trustRowMeta,
    },
    'vertical-ticker': {
        type: 'vertical-ticker',
        schema: verticalTickerSchema,
        Component: dispatcher('vertical-ticker'),
        presets: verticalTickerPresets,
        meta: verticalTickerMeta,
    },
    video: {
        type: 'video',
        schema: videoSchema,
        Component: dispatcher('video'),
        presets: videoPresets,
        meta: videoMeta,
    },
    'video-with-text': {
        type: 'video-with-text',
        schema: videoWithTextSchema,
        Component: dispatcher('video-with-text'),
        presets: videoWithTextPresets,
        meta: videoWithTextMeta,
    },
    'content-tabs': {
        type: 'content-tabs',
        schema: contentTabsSchema,
        Component: dispatcher('content-tabs'),
        presets: contentTabsPresets,
        meta: contentTabsMeta,
    },
    'tabbed-columns': {
        type: 'tabbed-columns',
        schema: tabbedColumnsSchema,
        Component: dispatcher('tabbed-columns'),
        presets: tabbedColumnsPresets,
        meta: tabbedColumnsMeta,
    },
};
//# sourceMappingURL=registry.lazy.gen.js.map